import React, { ReactElement, ReactNode } from 'react'
import { BLOCKS, INLINES, MARKS, Node } from '@contentful/rich-text-types'
import { renderRichText } from 'gatsby-source-contentful/rich-text'
import { filterEmptyNodes } from './RichText.utils'
import { ContentfulType, IRichText } from './types'
import LinkConverter from '../LinkConverter'

// MUI\
import { Theme } from '@mui/material/styles'
import Box from '@mui/material/Box'
import Divider from '@mui/material/Divider'
import Typography from '@mui/material/Typography'
import SvgIcon from '@mui/material/SvgIcon'
import External_link from '@tdcerhverv/parrotfish/dist/icons/Icons/External_link.svg'
import Image from '../Image'
import Iframe from '../LinkConverter/components/Iframe'
import useMobileDisplay from '../../hooks/useMobileDisplay'
import CookieComplianceWrapper from '../CookieComplianceWrapper'
import getIdFromString from '../../utils/getIdFromString'
import { grey } from '@mui/material/colors'
import JsonModule from '../JsonModule'

const styles = {
  root: ({ breakpoints, palette }: Theme) => ({
    '& .MuiButton-root': {
      marginRight: '16px',
      '&:last-child': {
        marginRight: 0,
      },
      [breakpoints.down('sm')]: {
        marginBottom: '16px',
      },
    },
    '& ul': {
      padding: 0,
      paddingLeft: '18px',
      '& li': {
        marginBottom: '16px',
        '& p': {
          margin: 0,
        },
      },
    },
    '& ol': {
      padding: 0,
      '& > li': {
        counterIncrement: 'styled-counter',
        listStyle: 'none',
        marginBottom: '16px',
        paddingLeft: '48px',
        position: 'relative',
        '&:last-child': {
          marginBottom: 0,
        },
        '& > *': {
          display: 'block',
          '&:last-child': {
            marginBottom: 0,
          },
        },
        '&:before': {
          content: 'counter(styled-counter)',
          backgroundColor: palette.primary.main,
          color: palette.common.white,
          display: 'inline-flex',
          borderRadius: '50%',
          width: '24px',
          height: '24px',
          justifyContent: 'center',
          alignItems: 'center',
          position: 'absolute',
          top: 0,
          left: 0,
        },
      },
    },
  }),
  h2: {
    scrollMarginTop: '80px',
    marginBottom: '.5em',
    wordBreak: 'break-word',
    '&:only-child': {
      marginTop: '-5px',
    },
    '&:first-child': {
      marginTop: 0,
    },
  },
  h3: {
    wordBreak: 'break-word',
    marginBottom: '.7em',
  },
  h4: {
    wordBreak: 'break-word',
    marginBottom: '1em',
  },
  h5: {
    wordBreak: 'break-word',
    marginBottom: '1em',
  },
  h6: {
    wordBreak: 'break-word',
    marginBottom: '1em',
  },
  divider: {
    marginBottom: '1em',
  },
  underline: {
    textDecoration: 'underline',
  },
  linkIcon: {
    width: '11px',
    height: '11px',
    marginLeft: '11px',
  },
  table: ({ breakpoints, palette }: Theme) => ({
    '& table': {
      width: '100%',
      borderColor: palette.common.white,
      '& p': {
        marginBottom: 0,
      },
      '& th': {
        padding: '5px',
        backgroundColor: 'primary.main',
        color: 'common.white',
      },
      '& td': {
        padding: '5px',
      },
      '& tr:nth-child(even) td': {
        backgroundColor: grey[200],
      },
      [breakpoints.down('xs')]: {
        display: 'block',
        overflowX: 'auto',
      },
    },
  }),
}

const urlStartswith = (url: string, identifier: string) => {
  const protocolRegex = new RegExp('^http[s]?://' + identifier, 'i')

  return protocolRegex.test(url)
}

const removeLastEmptyNode = (raw: string) => {
  /**
   * Removes the last node if the value is empty, preventing a breaktag at the end of the content.
   */
  const parsedData = JSON.parse(raw)
  const newContent = parsedData.content.filter((item: any, index: any) => {
    if (item.content.length < 1) return item
    if (parsedData.content.length - 1 === index) {
      return item.content.find((content: any) => content.value !== '')
    }
    return item
  })
  const dataArray = {
    ...parsedData,
    content: newContent,
  }
  return JSON.stringify(dataArray) as string
}

const RichText = ({
  raw,
  references,
  renderNode,
  linkIcon,
  sx = [],
  parentModule,
  renderMark,
  renderText,
  componentMap,
  propsMap,
}: IRichText): ReactElement => {
  if (!raw) {
    return <></>
  }
  const isMobile = useMobileDisplay('md')
  const filteredRaw = removeLastEmptyNode(raw!)

  const richTextData = {
    raw: filteredRaw,
    references,
  }
  const defaultNodeRender = {
    [BLOCKS.HEADING_1]: (node: Node, text: string) => {
      const element = (
        <Typography component="h2" variant={isMobile ? 'h3' : 'h1'}>
          {text}
        </Typography>
      )
      return filterEmptyNodes(node, element)
    },
    [BLOCKS.HEADING_2]: (node: Node, text: Array<any>) => {
      const richTextArray = text[0]
      const headingText = richTextArray?.['props']
        ? richTextArray?.['props']?.children[1]
        : richTextArray?.filter((element: any) => typeof element === 'string' && element.length > 0)[0]
      const id = headingText && getIdFromString(headingText)
      const element = (
        <Typography component="h2" variant={'h2'} sx={styles.h2} data-scroll-id={id}>
          {text}
        </Typography>
      )
      return filterEmptyNodes(node, element)
    },
    [BLOCKS.HEADING_3]: (node: Node, text: string) => {
      const element = (
        <Typography component="h3" variant={'h3'} sx={styles.h3}>
          {text}
        </Typography>
      )
      return filterEmptyNodes(node, element)
    },
    // h4 -> h6 are awaiting design for what they should render as.
    [BLOCKS.HEADING_4]: (node: Node, text: string) => {
      const element = (
        <Typography sx={styles.h4} component="h4" variant={'h4'}>
          {text}
        </Typography>
      )
      return filterEmptyNodes(node, element)
    },
    [BLOCKS.HEADING_5]: (node: Node, text: string) => {
      const element = (
        <Typography sx={styles.h5} component="h5" variant={'h5'}>
          {text}
        </Typography>
      )
      return filterEmptyNodes(node, element)
    },
    [BLOCKS.HEADING_6]: (node: Node, text: string) => {
      const element = (
        <Typography sx={styles.h6} component="h6" variant={'h6'}>
          {text}
        </Typography>
      )
      return filterEmptyNodes(node, element)
    },
    [BLOCKS.HR]: () => <Divider sx={styles.divider} />,
    [BLOCKS.PARAGRAPH]: (node: Node, text: string) => {
      const element = (
        <Typography variant={'body1'} paragraph>
          {text}
        </Typography>
      )
      return filterEmptyNodes(node, element)
    },
    [BLOCKS.TABLE]: (node: Node, children: ReactNode) => (
      <Box sx={styles.table}>
        <table>
          <tbody>{children}</tbody>
        </table>
      </Box>
    ),
    [BLOCKS.TABLE_HEADER_CELL]: (node: Node, children: ReactNode) => <th scope="col">{children}</th>,
    [BLOCKS.TABLE_ROW]: (node: Node, children: ReactNode) => <tr>{children}</tr>,
    [BLOCKS.TABLE_CELL]: (node: Node, children: ReactNode) => <td scope="col">{children}</td>,
    [INLINES.HYPERLINK]: (node: Node, text: string) => {
      const url = node.data?.uri
      const isTdc = urlStartswith(url, 'tdc.dk')
      if (!url) {
        throw new Error('A Hyperlink must have a url provided')
      }
      if (url.includes('youtube.com/embed/')) {
        return (
          <CookieComplianceWrapper cookieConsent="Statistic">
            <Iframe
              linkHref={url}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen={true}
              title="YouTube video player"
              frameBorder="0"
              width={560}
            />
          </CookieComplianceWrapper>
        )
      }
      const element = (
        <>
          <LinkConverter {...(!isTdc && { target: '_blank' })} linkHref={url} isTooltip={true}>
            {text}
          </LinkConverter>
          {linkIcon && <SvgIcon sx={styles.linkIcon} component={External_link} viewBox="0 0 48 48" />}
        </>
      )
      return filterEmptyNodes(node, element)
    },
    [INLINES.ENTRY_HYPERLINK]: (node: Node, children: string) => {
      return <LinkConverter linkHref={`/${node?.data?.target?.slug}`}>{children}</LinkConverter>
    },
    [INLINES.ASSET_HYPERLINK]: () => {
      console.warn('Use Component: FileLink instead of Asset Hyperlinks!')
      return <></>
    },
    [INLINES.EMBEDDED_ENTRY]: (node: Node) => {
      // Ensure 'node.data.target' exists and has the required structure
      if (!node?.data?.target || !node.data.target.internal) {
        console.error('Invalid or missing embedded entry target:', node)
        return null
      }

      const {
        internal: { type },
        ...props
      } = node.data.target

      const Component = componentMap?.[type as ContentfulType]
      const castPropsToType = propsMap?.[type as ContentfulType]

      if (!Component) {
        console.error(`No component found for type: ${type}`)
        return null
      }

      if (!castPropsToType) {
        console.warn(`No props mapper found for type: ${type}. Using raw props.`)
        return <Component {...props} />
      }

      return <Component {...castPropsToType(props)} />
    },
    [BLOCKS.EMBEDDED_ENTRY]: (node: Node) => {
      // Ensure 'node.data.target' exists and has the required structure
      if (!node?.data?.target || !node.data.target.internal) {
        console.error('Invalid or missing embedded entry target:', node)
        return null
      }

      const {
        internal: { type },
        ...props
      } = node.data.target

      const Component = componentMap?.[type as ContentfulType]
      const castPropsToType = propsMap?.[type as ContentfulType]

      if (!Component) {
        console.error(`No component found for type: ${type}`)
        return null
      }

      if (!castPropsToType) {
        console.warn(`No props mapper found for type: ${type}. Using raw props.`)
        return (
          <div style={{ marginBottom: 16 }}>
            <Component {...props} />
          </div>
        )
      }

      return (
        <div style={{ marginBottom: 16 }}>
          <Component {...castPropsToType(props)} />
        </div>
      )
    },
    [BLOCKS.EMBEDDED_ASSET]: (node: Node) => {
      const file = node?.data?.target?.file
      if (!file) return null

      if (file && file.contentType === 'application/json') {
        const url = node?.data?.target?.file?.url
        return <JsonModule url={url} />
      }

      //For now, we assume that if the file is not JSON, that it is an image.
      return <Image {...node?.data?.target} />
    },
  }
  const defaultTextRender = (text: string) => text.split('\n').flatMap((text, i) => [i > 0 && <br key={i} />, text])
  const defaultMarkRender = {
    [MARKS.BOLD]: (text: string) => <strong>{text}</strong>, // replaces <b>
    [MARKS.ITALIC]: (text: string) => <em>{text}</em>, //replaces <i>
    [MARKS.UNDERLINE]: (text: string) => (
      <Typography component="span" sx={styles.underline}>
        {text}
      </Typography>
    ),
  }
  const options: object = {
    renderMark: {
      ...defaultMarkRender,
      ...renderMark,
    },
    renderNode: { ...defaultNodeRender, ...renderNode },
    renderText: (text: string) => {
      const customRenderedText = renderText ? renderText(text) : text

      const defaultRenderedText = defaultTextRender(customRenderedText as string)

      return defaultRenderedText
    },
  }

  return <Box sx={[styles.root, ...(Array.isArray(sx) ? sx : [sx])]}>{renderRichText(richTextData, options)}</Box>
}

export default RichText
